import axios from 'axios';

/**
 * SHER-215 — every localStorage key the sign-in flow writes (or that the app
 * later derives from the user's session). Single source of truth so signOut,
 * the Dashboard 401 fallback, and any future "force re-auth" code paths can't
 * drift out of sync and leave User A's state visible to User B.
 *
 * Adding a new key the sign-in flow writes? Add it here too.
 */
export const SESSION_LOCAL_STORAGE_KEYS = [
    'jwt',
    'password',
    'rawKeyResearcher',
    'keyResearcher',           // legacy alias still written by src/actions/index.js
    'type',
    'forceChangePassword',
    'name',
    'surnames',
    'uuid',
    'email',
    'uuidInvestigation',
    'rawKeyInvestigation',
    // SHER-215 (review follow-up) — sync bookkeeping written by patientsActions /
    // utils/index.jsx during a session. Leaving these means User B's incremental
    // sync would resume from User A's last event ID / last-update timestamp.
    'data_download',
    'data_download_date',
    'lastUpdatePatients',
    'patient_sync_state',
    'lastSyncTimestamp',
    'lastSyncedPatientCount',
    'lastEventId',
] as const;

/**
 * SHER-215 (review follow-up) — these prefixes match keys whose suffix is a
 * dynamic uuid (per-investigation). They have to be cleared by sweep, not by
 * exact match, because the SPA writes one entry per investigation the user has
 * touched in this session.
 *
 * `rawKeyInvestigation_<uuid>` is the most security-sensitive: it holds the
 * *decrypted* investigation AES key. Leaving these across signOut violates the
 * zero-knowledge invariant called out in CLAUDE.md and lets a subsequent user
 * on the same browser read User A's investigation keys directly from
 * localStorage.
 */
export const SESSION_LOCAL_STORAGE_KEY_PREFIXES = [
    'rawKeyInvestigation_',
    'lastEventId_',
] as const;

/**
 * SHER-215 — fully detach the SPA from the previous user's session.
 *
 * Pedro's repro: log out User A → log in User B → background paginations from
 * User A keep firing with User A's JWT, returning 403 + (Pedro reports)
 * missing CORS headers, surfacing as "CORS error" in the console. Root cause
 * was that signOut only cleared 5 localStorage keys and never reset axios's
 * default Authorization header, so every NEW request fired before signIn
 * (or fired in parallel with signIn) carried the stale JWT.
 *
 * This helper is the single place where we tear the session down. It does
 * NOT clear IndexedDB / patient cache — that's a separate concern owned by
 * `clearPatientsStore()` in src/db, called by the signOut redux action.
 */
export function clearSession(): void {
    for (const key of SESSION_LOCAL_STORAGE_KEYS) {
        try { localStorage.removeItem(key); } catch (_) { /* SSR / locked storage */ }
    }
    // Sweep dynamic-suffix keys (per-investigation entries — see prefix list above).
    // Snapshot keys() first because removeItem during iteration mutates the set.
    try {
        const allKeys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k) allKeys.push(k);
        }
        for (const k of allKeys) {
            if (SESSION_LOCAL_STORAGE_KEY_PREFIXES.some((p) => k.startsWith(p))) {
                try { localStorage.removeItem(k); } catch (_) { /* ignore */ }
            }
        }
    } catch (_) { /* never break sign-out on a localStorage hiccup */ }
    // Reset axios's Authorization default. Without this, requests started
    // *after* signOut but *before* the next signIn (route-guard validate,
    // background sync, etc.) keep using the previous user's JWT.
    try { delete (axios.defaults.headers as any).Authorization; } catch (_) { /* ignore */ }
    try { delete (axios.defaults.headers as any).authorization; } catch (_) { /* ignore */ }
    try {
        if (axios.defaults.headers.common) {
            delete (axios.defaults.headers.common as any).Authorization;
            delete (axios.defaults.headers.common as any).authorization;
        }
    } catch (_) { /* ignore */ }
}
